"use client"

import { useEffect, useState } from "react"
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts"
import { TrendingUp } from "lucide-react"
import { LineCanvas } from "@nivo/line"

// Custom Card Components
const Card = ({ children, className = "" }) => (
  <div className={`bg-white rounded-xl shadow-sm border border-gray-200 ${className}`}>{children}</div>
)

const CardContent = ({ children, className = "" }) => <div className={`p-6 ${className}`}>{children}</div>

// Custom Button Component
const Button = ({ children, variant = "default", size = "default", className = "", onClick, ...props }) => {
  const baseClasses = "px-3 py-1 rounded-md font-medium transition-colors"
  const variantClasses = {
    default: "bg-blue-600 text-white hover:bg-blue-700",
    ghost: "text-gray-600 hover:text-gray-900 hover:bg-gray-100",
  }
  const sizeClasses = {
    sm: "text-sm px-2 py-1",
    default: "text-sm px-3 py-1",
  }

  return (
    <button
      className={`${baseClasses} ${variantClasses[variant]} ${sizeClasses[size]} ${className}`}
      onClick={onClick}
      {...props}
    >
      {children}
    </button>
  )
}

const GrievanceTrend = ({ data, duration, setDuration,themes }) => {
  const [chartData, setChartData] = useState([])

  const filters = [
    { key: "Past Week", label: "Weekly" },
    { key: "Past Month", label: "Monthly" },
    { key: "Past Year", label: "Yearly" },
  ]

  useEffect(() => {
    if (data && data.durationCount) {
      setChartData(data.durationCount)
    }
  }, [data, duration])

  const total = chartData.reduce((sum, item) => sum + item.count, 0)
  const peak = chartData.reduce((max, item) => (item.count > max.count ? item : max), { count: 0, label: "" })
  const avg = chartData.length ? (total / chartData.length).toFixed(2) : 0
  const active = chartData.filter((item) => item.count > 0).length

  return (
    <Card className="h-[400px]">
      <CardContent className="p-6 h-full flex flex-col">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-bold text-color flex items-center gap-2">
            <TrendingUp size={24} className="text-blue-600" />
            Grievance Trend
          </h3>
          <div className="flex bg-gray-100 rounded-lg p-1">
            {filters.map((filter) => (
              <Button
                key={filter.key}
                variant={duration === filter.key ? "default" : "ghost"}
                size="sm"
                onClick={() => setDuration(filter.key)}
              >
                {filter.label}
              </Button>
            ))}
          </div>
        </div>

        <div className="flex-1 mb-6">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
              <XAxis dataKey="label" stroke={themes === 'darkMode' ? '#ffffff' : '#6b7280'} fontSize={12} />
              <YAxis fontSize={12} stroke={themes === 'darkMode' ? '#ffffff' : '#6b7280'}/>
              <Tooltip
                contentStyle={{
                  backgroundColor: "#fff",
                  border: "1px solid #e5e7eb",
                  borderRadius: "8px",
                  boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
                }}
              />
              <Line
                type="monotone"
                dataKey="count"
                stroke="#3B82F6"
                strokeWidth={3}
                dot={{ fill: "#3B82F6", strokeWidth: 2, r: 6 }}
                activeDot={{ r: 8, stroke: "#3B82F6", strokeWidth: 2 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div className="grid grid-cols-4 gap-4">
          <div className="text-center p-3 bg-blue-50 rounded-lg">
            <p className="text-2xl font-bold text-blue-600">{total}</p>
            <p className="text-sm text-blue-700">Total</p>
          </div>
          <div className="text-center p-3 bg-green-50 rounded-lg">
            <p className="text-2xl font-bold text-green-600">{peak.count}</p>
            <p className="text-sm text-green-700">Peak</p>
          </div>
          <div className="text-center p-3 bg-orange-50 rounded-lg">
            <p className="text-2xl font-bold text-orange-600">{avg}</p>
            <p className="text-sm text-orange-700">Average</p>
          </div>
          <div className="text-center p-3 bg-purple-50 rounded-lg">
            <p className="text-2xl font-bold text-purple-600">{active}</p>
            <p className="text-sm text-purple-700">Active</p>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

export default GrievanceTrend
